import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastHandlerService } from '../../../../shared/services/toast-handler.service';
import { BackendPaginationResponse } from '../../../../shared/models/backend-pagination-response';
import { environment } from '../../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { Valet } from '../models/valet';
import { VALET_DOCUMENT_STATUS, VALET_DOCUMENT_TYPE, ValetDocuments } from '../../../../common-modules/common-valet/models/valet-document';
import { ExportCsvService } from '../../../../shared/services/export-csv.service';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import {NbDialogService} from '@nebular/theme';
import {
  KazePhoneAlreadyTakenModalComponent
} from '../components/kaze-phone-already-taken-modal/kaze-phone-already-taken-modal.component';
import {
  AmountAvailableToWithhold,
  ValetWithholding
} from '../../../../common-modules/common-valet/models/valet-withholding';
import {
  CommonValetWithholdingDialogComponent
} from '../../../../common-modules/common-valet/components/common-valet-profile-form/common-valet-withholding-dialog/common-valet-withholding-dialog.component';
import { BackendFilteringObject } from 'src/app/shared/models/grid-config.interface';
import { AnomalyTypesGroup } from 'src/app/common-modules/common-mission/models/anomalyTypesGroup';
import { CommonMissionService } from 'src/app/common-modules/common-mission/services/common-mission.service';

declare type kazeError =  {
  error: string;
  value: number;
  message: string;
  label: string
}

@Injectable({
  providedIn: 'root'
})
export class AdminValetService {

  public currencyFormatter: Intl.NumberFormat;
  private anomalyTypesGroups: AnomalyTypesGroup[];

  constructor(private http: HttpClient,
              private exportService: ExportCsvService,
              private translateService: TranslateService,
              private dialogService: NbDialogService,
              private commonMissionService: CommonMissionService,
              private toastService: ToastHandlerService) {
    this.currencyFormatter = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'});
    this.anomalyTypesGroups = this.commonMissionService.getMissionAnomaliesTypesGroups();
  }

  public getValets(filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<Valet>> {
    return this.http.get(`${environment.backendUrl}admin/valets?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetFraudInfo(valetId: string): Promise<any> {
    const url = `${environment.backendUrl}admin/valets/get-fraud-info/${valetId}`;
    return this.http.get<any>(url).toPromise();
  }

  public getValetMissions(valetId: string): Promise<any> {
    const url = `${environment.backendUrl}admin/valets/get-missions-by-valet/${valetId}`;
    return this.http.get<any>(url).toPromise();
  }

  public exportValets(filteringObj?: BackendFilteringObject): Promise<void> {
    const filterParams = filteringObj ? `&${filteringObj.getBackendFiltering()}` : '';
    return this.http.get(`${environment.backendUrl}admin/valets?exportCSV=true${filterParams}`, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
        map((response: any) => {
          const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";\n]+)"?/)?.[1];
          const url = window.URL.createObjectURL(response.body);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(url);

          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.valet.services.valet.export_success')
          );
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getLastOfFirstGroup<T>(array: T[], key: keyof T, value: any) {
    for (let i = 0; i < array.length; i++) {
      if (array[i][key] === value) { // First occurrence of "value"
        for (let j = i; j < array.length; j++) {
          if (!array[j + 1] || array[j + 1][key] !== value) { // Next occurrence doesn't exist or is different
            return array[j];
          }
        }
      }
    }

    return null;
  }

  public getValet(valetId: string): Promise<Valet> {
    return this.http.get(`${environment.backendUrl}admin/valets/${valetId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public createValet(valet: Valet): Promise<Valet> {
    return this.http.post(`${environment.backendUrl}admin/valets`,
      valet)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.valet.services.valet.valet_created_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.manageError(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public updateValet(valetId: string, valet: Valet): Promise<Valet> {
    return this.http.put(`${environment.backendUrl}admin/valets/${valetId}`,
      valet)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.valet.services.valet.valet_updated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.manageError(err)
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetDocuments(valetId: string): Promise<ValetDocuments> {
    return this.http.get(`${environment.backendUrl}admin/valets/document/${valetId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public updateValetDocument(
    valetId: string,
    documentType: VALET_DOCUMENT_TYPE,
    validationState: VALET_DOCUMENT_STATUS,
    expiryDate: string | undefined,
    emittedDate: string | undefined
  ): Promise<ValetDocuments> {
    const body: any = {
      documents: [{
        documentType,
        status: validationState
      }]
    };
    if (expiryDate) {
      body.documents[0].expiryDate = expiryDate;
    }
    if (emittedDate) {
      body.documents[0].emittedDate = emittedDate;
    }
    return this.http.put(`${environment.backendUrl}admin/valets/document/${valetId}`, body)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
          this.translateService.instant('admin.modules.valet.services.valet.valet_document_updated_success')
        );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getActiveValets(onlyReady: boolean, availableOn?: string, getDoneMissionsCount?: boolean): Promise<any> {
    const params: any = {
      active: true
    };
    if (onlyReady) {
      params.ready = onlyReady;
    }
    if (availableOn) {
      params.availableOn = availableOn;
    }
    if (getDoneMissionsCount) {
      params.getDoneMissionsCount = getDoneMissionsCount;
    }
    return this.http.get(`${environment.backendUrl}admin/valets`, {
      params
    })
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetPositiveFeedbacks(valetId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}missions/customer-feedback/by-valet/${valetId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.get_valet_positive_feedbacks_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public generateContract(valetId: string): Promise<any> {
    return this.http.post(`${environment.backendUrl}admin/valets/generate-contract/${valetId}`, {})
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.valet.services.valet.valet_contract_generated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetAnomalies(valetId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}admin/valets/${valetId}/anomalies?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.get_mission_anomalies_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetInvoices(valetId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}valets/${valetId}/invoices?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.valet.valet_invoices.service.get_invoices_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteValet(valetId: string): Promise<BackendPaginationResponse<any>> {
    return this.http.delete(`${environment.backendUrl}admin/valets/${valetId}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('admin.modules.valet.services.valet.delete_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  private manageError(err) {
    const erreur = err.error?.message
    const jsonPart = erreur.replace(/^Erreur Kaze: /, '');

    try {
      const errorData = JSON.parse(jsonPart);
      const phoneTakenError = errorData.message?.phone?.find((subErr: kazeError) => subErr.error === 'taken');

      if (!!phoneTakenError) {
        this.dialogService.open(KazePhoneAlreadyTakenModalComponent, { context: { phone: phoneTakenError.value }})
      } else {
        this.toastService.showErrorToastsResponse(err);
      }
    } catch (e) {
      this.toastService.showErrorToastsResponse(err);
      console.error('Erreur lors du parsing JSON:', e);
    }
  }

  public getWithholdings(valetId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<ValetWithholding>> {
    return this.http.get(`${environment.backendUrl}admin/valets/${valetId}/withholdings?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.get_withholdings_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public hasActiveWithholdings(valetId: string): Promise<boolean> {
    return this.http.get(`${environment.backendUrl}admin/valets/${valetId}/withholdings/has-active-withholdings`)
      .pipe(
        map((res: any) => res.data.hasActiveWithholdings),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.get_withholdings_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public createWithholding(valetId: string, withholding: ValetWithholding) {
    return this.http.post(`${environment.backendUrl}admin/valets/${valetId}/withholdings`, withholding)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.create_withholdings_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public updateWithholding(valetId: string, withholdingId: string, withholding: ValetWithholding) {
    return this.http.put(`${environment.backendUrl}admin/valets/${valetId}/withholdings/${withholdingId}`, withholding)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.update_withholdings_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteWithholding(valetId: string, withholdingId: string) {
    return this.http.delete(`${environment.backendUrl}admin/valets/${valetId}/withholdings/${withholdingId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.delete_withholdings_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public openWithholdingDialog(withholding: ValetWithholding | null) {
    const dialog = this.dialogService.open(CommonValetWithholdingDialogComponent, {
      context: {
        withholding
      }
    });
    return dialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog,
          data: res
        };
      })
    );
  }

  public getAmountAvailableToWithhold(valetId: string): Promise<AmountAvailableToWithhold> {
    return this.http.get(`${environment.backendUrl}admin/valets/${valetId}/withholdings/amount-available-to-withhold`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.valet.valet_profile.withholdings.service.get_amount_available_to_withhold_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public formatMetropolis(row: any): string {
    return row.metropolis ? row.metropolis.name : '';
  }

  public formatMissionCount(row: any): string {
    return row.missionCount || '';
  }

  public exportExpensesAnalysisPageData(localData: any[], columns?: any[]): Promise<any> {
    try {
      let formattedExport;
      formattedExport = this.exportExpenseParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('modules.valet.valet_profile.withholdings.service.export_error')
      );
      return Promise.reject(error);
    }
  }

  private exportExpenseParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Prénom': row.firstName || '',
        'Nom': row.lastName || '',
        'Métropole': this.formatMetropolis(row),
        'Indice de fraude': row.isFraudulent ? 'TRUE' : 'FALSE',
        'Taux de qualité': row.qualityRate || null,
        'MISSIONS': row.missionCount || '',
      });
    });
    return formattedExport;
  }

  public exportValetsListPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportValetsListParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_error')
      );
      return Promise.reject(error);
    }
  }

  private exportValetsListParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Id': this.formatId(row),
        'Métropole': this.formatMetropolis(row),
        'Prénom': row.firstName || '',
        'Nom': row.lastName || '',
        'Numéro de téléphone': row.phone || '',
        'Email': row.email || '',
        'Statut': row.status || '',
        'Actif': row.active ? 'TRUE' : 'FALSE',
        'Prêt': row.ready ? 'TRUE' : 'FALSE',
        'Taux de qualité': row.qualityRate || null,
        'Niveau d\'expérience': row.levelOfExperience || null,
        'Date de création': this.formatCreationDate(row)
      });
    });
    return formattedExport;
  }

  public formatCreationDate(row: any): string {
    return moment(row.creationDate).format('DD-MM-YYYY');
  }

  public formatId(row: any): string {
    return row.previousId ? 'V' + row.previousId.substring(0, 8) : 'V' + row.id.substring(0, 8);
  }

  public formatStatus(row: any): string {
    return row.status === 'external-worker' ? 'External' : 'Internal';
  }

  public formatAmount(amount: any): string {
    return this.currencyFormatter.format(amount);
  }

  public formatAllowInExpenses(row: any): string {
    return row.allowInExpenses ? 'Oui' : 'Non';
  }

  public exportValetWithHoldingsPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportValetWithHoldingsParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_withholding_error')
      );
      return Promise.reject(error);
    }
  }

  private exportValetWithHoldingsParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Nom': row.name || '',
        'Total à retenir': this.formatAmount(row.totalAmount),
        'Restant à retenir': this.formatAmount(row.remainingAmount),
        'Total échéances': row.totalInstallments || '',
        'échéances restantes': row.remainingInstallments || '',
        'Autoriser le prélèvement sur les frais': this.formatAllowInExpenses(row)
      });
    });
    return formattedExport;
  }

  public formatMissionName(row: any): string {
    return 'M' + row.mission.id.substring(0, 8) + ' (' + row.mission.plateNumber + ')'
  }

  public formatAnomalyType(row: any): string {
    return this.anomalyTypesGroups
      .flatMap(g => g.types)
      .find(t => t.value === row.type)?.name || '';
  }

  public formatAnomalyComment(row: any): string {
    return row.comment ? row.comment.replace(/\n/g, '<br>') : '';
  }

  public formatAnomalyDocument(row: any): string {
    return (row && row.url) ? `<a href="${row.url}" target="_blank">${this.translateService.instant('modules.mission.mission_anomaly_grid.document')}</a>` : '';
  }

  public formatAnomalyDate(row: any): string {
    return moment(row.date).format('DD.MM.YYYY HH:mm:ss');
  }

  public formatAnomalyCreator(row: any): string {
    return row.creator ? (row.creator.firstName + ' ' + row.creator.lastName) : '';
  }

  public exportValetAnomaliesPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportValetAnomaliesParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_valet_anomalies_error')
      );
      return Promise.reject(error);
    }
  }

  private exportValetAnomaliesParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Type': this.formatAnomalyType(row),
        'Commentaire': this.formatAnomalyComment(row),
        'Documents': this.formatAnomalyDocument(row),
        'Id Mission': this.formatMissionName(row),
        'Date': this.formatAnomalyDate(row),
        'Créateur': this.formatAnomalyCreator(row)
      });
    });
    return formattedExport;
  }

  public formatPositiveFeedbackComment(row: any): string {
    return row.comment ? row.comment.replace(/\n/g, '<br>') : '';
  }

  public formatPositiveFeedbackDate(row: any): string {
    return moment(row.date).format('DD.MM.YYYY HH:mm:ss');
  }

  public formatPositiveFeedbackCreator(row: any): string {
    return row.creator ? (row.creator.firstName + ' ' + row.creator.lastName) : '';
  }

  public exportValetPositiveFeedbackPageData(localData: any[], admin: boolean = true): Promise<any> {
    try {
      let formattedExport = this.exportValetPositiveFeedbackParser(localData, admin);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_valet_positive_feedback')
      );
      return Promise.reject(error);
    }
  }

  private exportValetPositiveFeedbackParser(dataToExport: any[], admin: boolean = true): any[] {
    const formattedExport = [];
    if (admin) {
      dataToExport.forEach(row => {
        formattedExport.push({
          'Commentaire': this.formatPositiveFeedbackComment(row),
          'Id Mission': this.formatMissionName(row),
          'Date': this.formatPositiveFeedbackDate(row),
          'Créateur': this.formatPositiveFeedbackCreator(row)
        });
      });
    } else {
      dataToExport.forEach(row => {
        formattedExport.push({
          'Id Mission': this.formatMissionName(row),
          'Commentaire': this.formatPositiveFeedbackComment(row),
          'Date': this.formatPositiveFeedbackDate(row)
        });
      });
    }
    return formattedExport;
  }

  public formatValetInvoiceDate(row: any): string {
    return row.date ? moment(row.date).format('DD/MM/YYYY') : '';
  }

  public formatValetInvoiceAmount(row: any): string {
    return row.amount ? new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'}).format(row.amount) : '';
  }

  public exportValetInvoicesPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportValetInvoicesParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_valet_invoices_error')
      );
      return Promise.reject(error);
    }
  }

  private exportValetInvoicesParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Date': this.formatValetInvoiceDate(row),
        'Numéro de facture': row.invoiceReference,
        'Référence du paiement': row.paymentReference,
        'Montant': this.formatValetInvoiceAmount(row),
        'PDF': row.file.url
      });
    });
    return formattedExport;
  }
}
